import { useCallback, useId, useState } from 'react';
import type { FieldProps } from './TextField';
import { useFormField } from '../../hooks/useFormField';
import { FieldsetWrapper } from '../QuestionWrapper';

interface DatePart {
  value: string;
  maxLength: number;
  valid: boolean;
  dirty: boolean;
}

interface DateFieldState {
  date: DatePart;
  month: DatePart;
  year: DatePart;
}

function createInitialState(answer: unknown): DateFieldState {
  const state: DateFieldState = {
    date: { value: '', maxLength: 2, valid: false, dirty: false },
    month: { value: '', maxLength: 2, valid: false, dirty: false },
    year: { value: '', maxLength: 4, valid: false, dirty: false },
  };

  if (answer) {
    const parts = String(answer).split('/');
    state.date.value = parts[0] || '';
    state.month.value = parts[1] || '';
    state.year.value = parts[2] || '';
    state.date.valid = !!state.date.value;
    state.month.valid = !!state.month.value;
    state.year.valid = !!state.year.value;
  }

  return state;
}

function validatePart(value: string, maxLength: number): { validatedResult: boolean; autoCompletedValue: string } {
  let autoCompletedValue = value;
  let validatedResult = !!value && !isNaN(Number(value)) && value.length === maxLength;

  if (maxLength === 2) {
    validatedResult = !!value && !isNaN(Number(value));
    autoCompletedValue = value.length === 1 ? `0${value}` : value;
  }

  return { validatedResult, autoCompletedValue };
}

export function DateField({ fieldSchema, group }: FieldProps) {
  const field = useFormField(fieldSchema, group);
  const elemIdSuffix = useId().replace(/:/g, '');
  const [answerDateField, setAnswerDateField] = useState(() => createInitialState(field.answer));

  const updateAnswer = useCallback(
    (next: DateFieldState) => {
      if (next.date.valid && next.month.valid && next.year.valid) {
        field.setAnswer(`${next.date.value}/${next.month.value}/${next.year.value}`);
      }
    },
    [field]
  );

  const validateValue = (item: 'date' | 'month' | 'year') => {
    const part = answerDateField[item];
    const { validatedResult, autoCompletedValue } = validatePart(part.value, part.maxLength);

    const next = {
      ...answerDateField,
      [item]: {
        ...part,
        value: autoCompletedValue,
        valid: validatedResult,
        dirty: true,
      },
    };

    setAnswerDateField(next);
    updateAnswer(next);
  };

  const setPartValue = (item: 'date' | 'month' | 'year', value: string) => {
    setAnswerDateField((prev) => ({
      ...prev,
      [item]: { ...prev[item], value },
    }));
  };

  const showValidationMessage =
    (answerDateField.date.dirty && !answerDateField.date.valid) ||
    (answerDateField.month.dirty && !answerDateField.month.valid) ||
    (answerDateField.year.dirty && !answerDateField.year.valid);

  return (
    <FieldsetWrapper
      {...field}
      fieldSchema={fieldSchema}
      extraValidation={
        showValidationMessage ? (
          <div className="validation-msg active">Please enter a valid date (dd/mm/yyyy).</div>
        ) : undefined
      }
    >
      <div className="answer-group">
        <div
          className="answer field date flex"
          data-max-date={fieldSchema.maxDate}
          id={`${field.question.id}-${elemIdSuffix}`}
        >
          <label className="small-label right-margin-3 flex-basis--short-input">
            Day
            <input
              type="text"
              className={`date text full-width${answerDateField.date.dirty && !answerDateField.date.valid ? ' invalid' : ''}`}
              onBlur={() => validateValue('date')}
              value={answerDateField.date.value}
              onChange={(e) => setPartValue('date', e.target.value)}
              name={`${field.question.id}-${elemIdSuffix}--date`}
              placeholder="DD"
              maxLength={answerDateField.date.maxLength}
            />
          </label>
          <label className="small-label right-margin-3 flex-basis--short-input">
            Month
            <input
              type="text"
              className={`month text full-width${answerDateField.month.dirty && !answerDateField.month.valid ? ' invalid' : ''}`}
              onBlur={() => validateValue('month')}
              value={answerDateField.month.value}
              onChange={(e) => setPartValue('month', e.target.value)}
              name={`${field.question.id}-${elemIdSuffix}--month`}
              placeholder="MM"
              maxLength={answerDateField.month.maxLength}
            />
          </label>
          <label className="small-label right-margin-3 flex-basis--short-input">
            Year
            <input
              type="text"
              className={`year text full-width${answerDateField.year.dirty && !answerDateField.year.valid ? ' invalid' : ''}`}
              onBlur={() => validateValue('year')}
              value={answerDateField.year.value}
              onChange={(e) => setPartValue('year', e.target.value)}
              name={`${field.question.id}-${elemIdSuffix}--year`}
              placeholder="YYYY"
              maxLength={answerDateField.year.maxLength}
            />
          </label>
        </div>
      </div>
    </FieldsetWrapper>
  );
}
